<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { REVIEW_TYPE } from "@apps/reviews/constants";
    import ReviewsStorefrontValuesSettingsEntity from "@apps/reviews/entities/ReviewsStorefrontValuesSettingsEntity";
    import { REVIEWS_FORM_STEPS_NAMES } from "@apps/reviews/widgets/review_form_modal_widget/constants";

    import Button from "@components/Buttons/Button.svelte";
    import Icon from "@components/Icon.svelte";
    import ArrowIcon from "@components/icons/ArrowIcon.svelte";
    import StepperController from "@components/Stepper/StepperController";
    import StepperProgress from "@components/Stepper/StepperProgress.svelte";
    import T from "@components/T.svelte";

    import ReviewsWizardManager from "../../managers/ReviewsWizardManager";
    import AttributesQuery from "../../queries/AttributesQuery";
    import LeaveReviewQuery from "../../queries/LeaveReviewQuery";
    import {
        getNavProgressSteps,
        isShowNavProgress,
    } from "./navigationHelpers";

    export let reviewType: REVIEW_TYPE | undefined;
    export let isAuthenticated: boolean;
    export let leaveReviewQuery: LeaveReviewQuery;
    export let attributesQuery: AttributesQuery;
    export let reviewsWizardManager: ReviewsWizardManager;
    export let reviewsSettings: ReviewsStorefrontValuesSettingsEntity;
    export let stepperController: StepperController;

    const currentStep$ = stepperController.currentStep$;
    const leaveReviewStatus$ = leaveReviewQuery.leaveReviewStatus$;
    const attributesForm$ = attributesQuery.attributes$;

    const nextButton$ = reviewsWizardManager.nextButton$;
    const prevButton$ = reviewsWizardManager.prevButton$;

    $: currentStepName = $currentStep$?.name as REVIEWS_FORM_STEPS_NAMES;

    $: showNavProgress = isShowNavProgress({
        currentStepName,
        isAuthenticated,
        isAddedImages: false,
    });

    $: navProgressSteps = getNavProgressSteps({
        reviewType: reviewType,
        hasCustomForms:
            [
                ...$attributesForm$.customerAttributes,
                ...$attributesForm$.productAttributes,
            ].length > 0,
        isAuthenticated,
        isReviewsMediaEnabled: reviewsSettings.isReviewsMediaEnabled,
    });

    $: hideNav = [REVIEWS_FORM_STEPS_NAMES.RATING].includes(currentStepName);
</script>

{#if !hideNav}
    <div class="gw-rv-form-nav">
        <div class="gw-rv-form-nav__left">
            {#if $prevButton$ && $prevButton$.type === "prev"}
                <Button
                    type="button"
                    on:click={$prevButton$.onClick}
                    variant="text"
                    size="large"
                    class="gw-rv-form-nav__back-button"
                >
                    <Icon
                        class="gw-rv-form-nav__back-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                    >
                        <ArrowIcon />
                    </Icon>
                    <span class="gw-rv-form-nav__back-text">
                        <T
                            namespace="reviews.ReviewsFormModalWidget"
                            translationKey="back"
                        />
                    </span>
                </Button>
            {/if}
        </div>

        <div class="gw-rv-form-nav__center">
            {#if showNavProgress}
                <StepperProgress
                    stepsNames={navProgressSteps}
                    {currentStepName}
                />
            {/if}
        </div>

        <div class="gw-rv-form-nav__right">
            {#if $nextButton$}
                {#if $nextButton$.type === "next"}
                    <Button
                        type="button"
                        on:click={$nextButton$.onClick}
                        variant="opacityOnHover"
                        size="large"
                    >
                        <T
                            namespace="reviews.ReviewsFormModalWidget"
                            translationKey="next"
                        />
                    </Button>
                {:else if $nextButton$.type === "skip"}
                    <Button
                        type="button"
                        on:click={$nextButton$.onClick}
                        variant="opacityOnHover"
                        size="large"
                        disabled={!$nextButton$.isActive}
                        class="gw-rv-form-nav__skip-button"
                    >
                        <T
                            namespace="reviews.ReviewsFormModalWidget"
                            translationKey="skip"
                        />
                    </Button>
                {:else if $nextButton$.type === "submit"}
                    <Button
                        type="button"
                        variant="opacityOnHover"
                        size="large"
                        loading={$leaveReviewStatus$ === STATUS.LOADING}
                        disabled={!$nextButton$.isActive}
                        on:click={$nextButton$.onClick}
                    >
                        <T
                            namespace="reviews.ReviewsFormModalWidget"
                            translationKey="submit"
                        />
                    </Button>
                {:else if $nextButton$.type === "close"}
                    <Button
                        type="button"
                        on:click={$nextButton$.onClick}
                        variant="opacityOnHover"
                        size="large"
                    >
                        <T
                            namespace="reviews.ReviewsFormModalWidget"
                            translationKey="close"
                        />
                    </Button>
                {:else if $nextButton$.type === "continue"}
                    <Button
                        type="button"
                        on:click={$nextButton$.onClick}
                        variant="opacityOnHover"
                        size="large"
                    >
                        <T
                            namespace="reviews.ReviewsFormModalWidget"
                            translationKey="continue"
                        />
                    </Button>
                {/if}
            {/if}
        </div>
    </div>
{/if}

<style lang="postcss">
    .gw-rv-form-nav {
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        border-top: 1px solid #f2f4f7;
        padding: 2em 1.5em;

        @media screen and (max-width: 600px) {
            padding: 2em 0;
            gap: 1em;
        }

        &__left {
            display: flex;
            justify-content: flex-start;
            width: 20%;
            justify-self: flex-start;
        }

        &__center {
            display: block;
            width: 60%;
        }

        &__right {
            width: 20%;
            display: flex;
            justify-content: flex-end;
            justify-self: flex-end;
        }

        :global(&__back-icon) {
            transform: rotate(180deg);
        }

        &__back-text {
            display: inline-block;
        }
    }

    :global(.gw-btn.gw-button.gw-rv-form-nav__back-button) {
        @media all and (max-width: 600px) {
            display: none;
        }
    }

    :global(.gw-btn.gw-button.gw-rv-form-nav__skip-button) {
        @media all and (max-width: 600px) {
            display: none;
        }
    }
</style>
